import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superAdminApi } from '../services/super-admin-api'

export interface OrphanedAuthUser {
  auth_user_id: string
  email: string
  created_at: string
}

export interface ProfileWithoutAuth {
  profile_id: string
  email: string
  full_name: string | null
  created_at: string
}

export interface MemberWithoutProfessional {
  member_id: string
  profile_id: string
  email: string
  full_name: string | null
  role: string
  organization_id: string
  organization_name: string
  joined_at: string
}

export interface MaintenanceIssues {
  orphaned_auth_users: OrphanedAuthUser[]
  profiles_without_auth: ProfileWithoutAuth[]
  members_without_professional: MemberWithoutProfessional[]
}

export function useMaintenanceIssues() {
  return useQuery<MaintenanceIssues>({
    queryKey: ['super-admin', 'maintenance', 'issues'],
    queryFn: () => superAdminApi.get<MaintenanceIssues>('/maintenance/issues'),
    staleTime: 0,
  })
}

export function useFixOrphanedAuthUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (authUserId: string) =>
      superAdminApi.post<{ created_profile_id: string }>('/maintenance/fix/orphaned-auth-user', { auth_user_id: authUserId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'maintenance'] }),
  })
}

export function useFixProfileWithoutAuth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (profileId: string) =>
      superAdminApi.post<{ matched: boolean }>('/maintenance/fix/profile-without-auth', { profile_id: profileId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'maintenance'] }),
  })
}

export function useFixMemberWithoutProfessional() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (profileId: string) =>
      superAdminApi.post<{ created: boolean }>('/maintenance/fix/member-without-professional', { profile_id: profileId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'maintenance'] }),
  })
}

export function useFixAllMembersWithoutProfessional() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      superAdminApi.post<{ fixed: number }>('/maintenance/fix/all-members-without-professional'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'maintenance'] }),
  })
}
